import React from "react";
import { CoButton } from "components/CoButton";
import {
  ASK_PAPER_CREDITS_PER_MONTH_FREE_USER,
  DEEP_SEARCH_CREDITS_PER_MONTH_FREE_USER,
  PRO_SEARCH_CREDITS_PER_MONTH_FREE_USER,
  QUICK_SEARCH_CREDITS_PER_MONTH_FREE_USER,
  SNAPSHOT_CREDITS_PER_MONTH_FREE_USER,
} from "constants/config";
import { SubscriptionPlan } from "enums/subscription-plans";
import { SubscriptionPlanIcon } from "features/Pricing/utils";
import useCredits from "features/User/hooks/useCredits";
import { MixpanelActionSource } from "helpers/services/mixpanel/events";
import useLabels from "hooks/useLabels";
import { CreditedFeature } from "store/slices/subscription";
import { usePremiumFeatureModal } from "../PremiumFeatureModal";
import { FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT } from "features/SearchResultsList/components/SearchResult/constants";

type OutOfCreditsProps = {
  feature: CreditedFeature;
  preventEventDefault?: boolean;
  stopEventPropagation?: boolean;
  bottomRightComponent?: React.ReactNode;
  containerClassName?: string;
};

const featureToActionSource = {
  [CreditedFeature.STUDY_SNAPSHOT]: MixpanelActionSource.StudySnapshot,
  [CreditedFeature.CONSENSUS_SNAPSHOT]: MixpanelActionSource.ConsensusMeter,
  [CreditedFeature.ASK_PAPER]: MixpanelActionSource.AskPaper,
  [CreditedFeature.QUICK_SEARCH]: MixpanelActionSource.QuickSearch,
  [CreditedFeature.PRO_SEARCH]: MixpanelActionSource.ProSearch,
  [CreditedFeature.DEEP_SEARCH]: MixpanelActionSource.DeepSearch,
  [CreditedFeature.TABLE_EXTRACTION]: MixpanelActionSource.ResultsTableView,
};

/**
 * @component OutofCredits
 * @description Prompt the user to upgrade to premium when they are out of credits.
 */
function OutOfCredits({
  feature,
  preventEventDefault,
  stopEventPropagation,
  bottomRightComponent,
  containerClassName = "",
}: OutOfCreditsProps) {
  const [pageLabels, featureLabels, premiumDescription] = useLabels(
    "out-of-credits",
    `credits.features.${feature}`,
    "subscription-overlay.premium-description"
  );

  const { openPremiumFeatureModal } = usePremiumFeatureModal();

  const onClickUpgradeToPremium = (e: React.MouseEvent) => {
    preventEventDefault && e.preventDefault();
    stopEventPropagation && e.stopPropagation();
    openPremiumFeatureModal({
      feature,
      actionSource: featureToActionSource[feature],
    });
  };

  let MAX_CREDITS: number;
  switch (feature) {
    case CreditedFeature.STUDY_SNAPSHOT:
    case CreditedFeature.CONSENSUS_SNAPSHOT:
      MAX_CREDITS = SNAPSHOT_CREDITS_PER_MONTH_FREE_USER;
      break;
    case CreditedFeature.PRO_SEARCH:
      MAX_CREDITS = PRO_SEARCH_CREDITS_PER_MONTH_FREE_USER;
      break;
    case CreditedFeature.ASK_PAPER:
      MAX_CREDITS = ASK_PAPER_CREDITS_PER_MONTH_FREE_USER;
      break;
    case CreditedFeature.QUICK_SEARCH:
      // Quick search is not limited for a free user, so this should never be invoked.
      MAX_CREDITS = QUICK_SEARCH_CREDITS_PER_MONTH_FREE_USER ?? 0;
      break;
    case CreditedFeature.DEEP_SEARCH:
      MAX_CREDITS = DEEP_SEARCH_CREDITS_PER_MONTH_FREE_USER;
      break;
    case CreditedFeature.TABLE_EXTRACTION:
      // This should never be invoked, free users can always extract data for the first 3 papers.
      MAX_CREDITS = FREE_USER_SNAPSHOT_AUTO_FETCH_LIMIT ?? 0;
      break;
  }

  const { isUsageSet, creditRefreshDate } = useCredits();

  if (!isUsageSet) {
    return null;
  }

  return (
    <div className={containerClassName}>
      <h1 className="mb-2 base-md">
        {pageLabels["title"].replace("{FEATURE}", featureLabels["credit-type"])}
      </h1>
      <p className="sm-normal">
        {
          <span
            dangerouslySetInnerHTML={{
              __html: pageLabels["description"]
                .replace("{FEATURE_PLURAL}", featureLabels["credit-type"])
                .replace("{MAX_CREDITS}", MAX_CREDITS)
                .replace("{RESET_DATE}", creditRefreshDate),
            }}
          ></span>
        }{" "}
        {feature === CreditedFeature.PRO_SEARCH
          ? featureLabels["cta-text"]
          : premiumDescription}
      </p>
      <div className="flex justify-between items-center mt-2.5">
        <CoButton
          icon={
            <SubscriptionPlanIcon plan={SubscriptionPlan.PREMIUM} size="sm" />
          }
          label={pageLabels["upgrade-to-premium"]}
          onClick={onClickUpgradeToPremium}
          size="sm"
        />
        {bottomRightComponent}
      </div>
    </div>
  );
}

export default OutOfCredits;
